import { useState, useEffect, useCallback, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import PageLoading from '../common/PageLoading';
import { Menu, User, LogOut, CalendarDays, ExternalLink } from 'lucide-react';
import Sidebar from './Sidebar';
import { LayoutProvider } from './LayoutContext';
import { useAppSidebarCollapsed } from '../../hooks/useAppSidebarCollapsed';
import LessonsLearnedButton from './LessonsLearnedButton';
import FeedbackButton from './FeedbackButton';
import LastUpdatedBadge from './LastUpdatedBadge';
import { useAuth } from '../../hooks/useAuth';
import { useMe } from '../../hooks/useMe';
import { useTheme } from '../../hooks/useTheme';
import { SearchBar, SearchModal } from '../search';
import SkipLink from '../common/SkipLink';
import VisuallyHidden from '../common/VisuallyHidden';
import YearSwitcher from './YearSwitcher';
import DevBanner from './DevBanner';
import YearOverrideBanner from './YearOverrideBanner';
import { useActiveYear } from '../../contexts/ActiveYearContext';
import { useMilestones } from '../../hooks/useMilestones';
import { daysFromToday } from '../../lib/dateOnly';

function ConferenceCountdownChip() {
  const { year, conferenceDate } = useActiveYear();
  const { data: milestones } = useMilestones();
  const conferenceMilestone = milestones?.find(
    (m) =>
      m.type === 'milestone' &&
      typeof m.title === 'string' &&
      m.title.trim().toLowerCase() === 'conference day' &&
      !!m.dueDate,
  );
  const effectiveDate = conferenceMilestone?.dueDate ?? conferenceDate;
  if (!year || !effectiveDate) return null;
  const days = daysFromToday(effectiveDate);
  if (Number.isNaN(days)) return null;

  let label: string;
  if (days > 1) label = `${days} days to conference`;
  else if (days === 1) label = `Tomorrow: conference day`;
  else if (days === 0) label = `Today: conference day`;
  else label = `Conference — that's a wrap`;

  return (
    <span
      className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border border-primary-200 dark:border-primary-900/60 bg-primary-50 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200"
      aria-label={label}
      title={label}
    >
      <CalendarDays className="w-3.5 h-3.5" aria-hidden="true" />
      <span className="whitespace-nowrap">{label}</span>
    </span>
  );
}

export default function Layout() {
  return (
    <LayoutProvider>
      <LayoutInner />
    </LayoutProvider>
  );
}

function LayoutInner() {
  const { user, logout } = useAuth();
  const { me } = useMe();
  // Keep the theme hook mounted at the app root so the stored preference is
  // applied and the system-preference listener is active on every page.
  useTheme();
  const { appSidebarCollapsed } = useAppSidebarCollapsed();
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
  };

  const openSearchModal = useCallback(() => {
    setIsSearchModalOpen(true);
  }, []);

  const closeSearchModal = useCallback(() => {
    setIsSearchModalOpen(false);
  }, []);

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  // Close sidebar on Escape key
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        openSearchModal();
      }
      if (e.key === 'Escape' && isSidebarOpen) {
        closeSidebar();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [openSearchModal, isSidebarOpen, closeSidebar]);

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isSidebarOpen]);

  // Prefer first name from Cognito attributes; fall back to email/username only
  // if given_name isn't set on the account.
  const displayName =
    me?.given_name?.trim() ||
    me?.email ||
    user?.signInDetails?.loginId ||
    user?.username ||
    'User';

  return (
    <div className="flex flex-col h-full">
      <SkipLink targetId="main-content" />
      <DevBanner />
      <YearOverrideBanner />

      <div className="flex flex-1 min-h-0">
      {/* Mobile sidebar backdrop */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar — always visible on lg+, slide-in on mobile */}
      <div
        className={`
          fixed inset-y-0 left-0 z-40 lg:relative lg:z-auto
          transform transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
      >
        <Sidebar
          onClose={closeSidebar}
          isCollapsed={!isSidebarOpen && appSidebarCollapsed}
        />
      </div>

      {/* Main content area */}
      <div className="flex flex-col flex-1 min-w-0">
        <header
          className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 md:px-6 py-3 flex items-center gap-3"
          role="banner"
        >
          {/* Left zone: hamburger (mobile) + countdown + year switcher */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="lg:hidden flex-shrink-0 p-2 -ml-1 rounded-lg text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              aria-label="Open navigation menu"
              aria-expanded={isSidebarOpen}
              aria-controls="sidebar-nav"
            >
              <Menu className="w-5 h-5" aria-hidden="true" />
            </button>
            <ConferenceCountdownChip />
            <LastUpdatedBadge />
            <div className="hidden sm:block">
              <YearSwitcher />
            </div>
          </div>

          {/* Center zone: search bar + BSides site link — hidden on mobile to
              prevent header overflow on ~360 px phones now that utility buttons
              are visible there. Both remain fully accessible via Cmd/Ctrl-K
              (search) and the Settings page sign-out. */}
          <div className="hidden sm:flex flex-1 justify-center items-center gap-2">
            <SearchBar onResultSelect={closeSearchModal} />
            <a
              href="https://bsidesboulder.org"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 px-2.5 py-2 rounded-lg text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors min-h-[44px] flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              aria-label="Open BSides Boulder website"
              title="Open BSides Boulder website"
            >
              <ExternalLink className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
              <span className="text-sm hidden sm:inline">bsidesboulder.org</span>
            </a>
          </div>

          {/* Right zone: feedback + lessons learned + user info + sign out.
              On mobile: search/link/year/sign-out are hidden, so Feedback and
              LessonsLearned are surfaced here. Sign-out is sm+-only; mobile users
              sign out via the Settings page. */}
          <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
            <div className="flex items-center gap-2 md:gap-4">
              <FeedbackButton />
              <LessonsLearnedButton />
            </div>
            <div
              className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 min-w-0"
              aria-label={`Logged in as ${displayName}`}
            >
              <User className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
              <span className="text-sm truncate hidden sm:inline max-w-[120px]">
                {displayName}
              </span>
            </div>
            <button
              onClick={handleLogout}
              className="hidden sm:flex items-center gap-1.5 text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 transition-colors min-h-[44px] px-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              aria-label="Sign out of your account"
            >
              <LogOut className="w-4 h-4" aria-hidden="true" />
              <span className="text-sm hidden sm:inline">Sign out</span>
            </button>
          </div>
        </header>

        <main
          id="main-content"
          className="flex-1 overflow-auto p-4 md:p-6"
          role="main"
          tabIndex={-1}
        >
          <VisuallyHidden as="h2">Main content</VisuallyHidden>
          {/* Pages are lazy-loaded (code splitting); the spinner renders in the
              content area so the header/sidebar stay put while a chunk loads. */}
          <Suspense fallback={<PageLoading />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* Search Modal */}
      <SearchModal isOpen={isSearchModalOpen} onClose={closeSearchModal} />
      </div>
    </div>
  );
}
